/**
 * Renderer Vol3D: textura 3D WebGL2 + ray-march (MIP o DVR con LUT 1D).
 * Si no hay WebGL2 / TEXTURE_3D, el viewport cae al MIP CPU.
 */
import { mpr3dViewBasis } from './mpr3dCamera'
import { buildPresetTf } from './mpr3dPresets'

const VS = `#version 300 es
precision highp float;
const vec2 POS[3] = vec2[3](vec2(-1.0, -1.0), vec2(3.0, -1.0), vec2(-1.0, 3.0));
out vec2 vUv;
void main() {
  vec2 p = POS[gl_VertexID];
  vUv = p * 0.5 + 0.5;
  gl_Position = vec4(p, 0.0, 1.0);
}
`

const FS = `#version 300 es
precision highp float;
precision highp sampler3D;

uniform sampler3D uVol;
uniform sampler2D uTf;
uniform vec3 uFwd;
uniform vec3 uRight;
uniform vec3 uUp;
uniform float uZoom;
uniform vec2 uPan;
uniform float uAspect;
uniform int uSteps;
uniform int uMode;
uniform int uDecapado;
uniform int uLight;
uniform vec3 uLightView;
uniform int uShadeMode;
uniform vec3 uClip;
uniform vec3 uKeep;
uniform vec3 uClipMask;
uniform float uSlab;
uniform float uInvN;
uniform vec3 uBox;
uniform vec3 uMark;
uniform int uMarkOn;
uniform float uSharp;
uniform sampler3D uHu;
uniform sampler3D uMask;
uniform int uHuOn;
uniform int uMaskOn;
uniform float uWw;
uniform float uWc;
uniform int uVolOff;

in vec2 vUv;
out vec4 fragColor;

vec3 toTex(vec3 p) {
  return (p - vec3(0.5)) / max(uBox, vec3(0.08)) + vec3(0.5);
}

bool insideClip(vec3 pw) {
  if (uDecapado == 0) return true;
  vec3 p = toTex(pw);
  // Slab: banda ±uSlab alrededor del plano (0 = hemicorte).
  if (uSlab > 0.0005) {
    if (uClipMask.x > 0.5 && abs(p.x - uClip.x) > uSlab) return false;
    if (uClipMask.y > 0.5 && abs(p.y - uClip.y) > uSlab) return false;
    if (uClipMask.z > 0.5 && abs(p.z - uClip.z) > uSlab) return false;
    return true;
  }
  if (uClipMask.x > 0.5) {
    if (uKeep.x > 0.0 && p.x < uClip.x) return false;
    if (uKeep.x < 0.0 && p.x > uClip.x) return false;
  }
  if (uClipMask.y > 0.5) {
    if (uKeep.y > 0.0 && p.y < uClip.y) return false;
    if (uKeep.y < 0.0 && p.y > uClip.y) return false;
  }
  if (uClipMask.z > 0.5) {
    if (uKeep.z > 0.0 && p.z < uClip.z) return false;
    if (uKeep.z < 0.0 && p.z > uClip.z) return false;
  }
  return true;
}

vec2 hitBox(vec3 ro, vec3 rd) {
  vec3 bmin = vec3(0.5) - 0.5 * uBox;
  vec3 bmax = vec3(0.5) + 0.5 * uBox;
  vec3 invd = 1.0 / rd;
  vec3 t0 = (bmin - ro) * invd;
  vec3 t1 = (bmax - ro) * invd;
  vec3 tsm = min(t0, t1);
  vec3 tbg = max(t0, t1);
  float tN = max(max(tsm.x, tsm.y), tsm.z);
  float tF = min(min(tbg.x, tbg.y), tbg.z);
  return vec2(tN, tF);
}

float windowHu(vec3 t) {
  vec2 rg = textureLod(uHu, t, 0.0).rg;
  float hu = rg.r * 255.0 * 256.0 + rg.g * 255.0 - 32768.0;
  return clamp((hu - (uWc - 0.5 * uWw)) / max(uWw, 1.0), 0.0, 1.0);
}

float sampleRaw(vec3 p) {
  vec3 t = toTex(p);
  if (any(lessThan(t, vec3(0.0))) || any(greaterThan(t, vec3(1.0)))) return 0.0;
  if (uMaskOn == 1) {
    if (textureLod(uMask, t, 0.0).r < 0.5) return 0.0;
  }
  return uHuOn == 1 ? windowHu(t) : textureLod(uVol, t, 0.0).r;
}

float sampleVol(vec3 p) {
  float lin = sampleRaw(p);
  if (lin < 0.028) return 0.0;
  if (uSharp < 0.02) return lin;
  ivec3 sz = textureSize(uVol, 0);
  vec3 t = toTex(p);
  ivec3 iv = ivec3(floor(clamp(t, 0.0, 0.999) * vec3(sz)));
  iv = clamp(iv, ivec3(0), sz - ivec3(1));
  float nn = uHuOn == 1 ? windowHu((vec3(iv) + 0.5) / vec3(sz)) : texelFetch(uVol, iv, 0).r;
  return mix(lin, nn, clamp(uSharp * 0.28, 0.0, 0.55));
}

// Blur 1 vóxel solo entre tejido (no mezcla aire → sin halo del AABB).
float sampleSmooth(vec3 p) {
  float c = sampleRaw(p);
  if (c < 0.028) return 0.0;
  vec3 e = uInvN * max(uBox, vec3(0.08));
  float acc = c * 3.0;
  float w = 3.0;
  float n;
  n = sampleRaw(p + vec3(e.x, 0.0, 0.0)); if (n >= 0.028) { acc += n; w += 1.0; }
  n = sampleRaw(p - vec3(e.x, 0.0, 0.0)); if (n >= 0.028) { acc += n; w += 1.0; }
  n = sampleRaw(p + vec3(0.0, e.y, 0.0)); if (n >= 0.028) { acc += n; w += 1.0; }
  n = sampleRaw(p - vec3(0.0, e.y, 0.0)); if (n >= 0.028) { acc += n; w += 1.0; }
  n = sampleRaw(p + vec3(0.0, 0.0, e.z)); if (n >= 0.028) { acc += n; w += 1.0; }
  n = sampleRaw(p - vec3(0.0, 0.0, e.z)); if (n >= 0.028) { acc += n; w += 1.0; }
  return acc / max(w, 1.0);
}

vec3 densGrad(vec3 p) {
  vec3 e = uInvN * max(uBox, vec3(0.08)) * 1.85;
  return vec3(
    sampleRaw(p + vec3(e.x, 0.0, 0.0)) - sampleRaw(p - vec3(e.x, 0.0, 0.0)),
    sampleRaw(p + vec3(0.0, e.y, 0.0)) - sampleRaw(p - vec3(0.0, e.y, 0.0)),
    sampleRaw(p + vec3(0.0, 0.0, e.z)) - sampleRaw(p - vec3(0.0, 0.0, e.z))
  );
}

// Unsharp 6-vecinos a 2 vóxeles: realza detalle que el downsample había suavizado.
float sampleSharp(vec3 p) {
  float c = sampleVol(p);
  if (uSharp < 0.02) return c;
  vec3 e = uInvN * max(uBox, vec3(0.08)) * 2.0;
  float blur = (
    sampleVol(p + vec3(e.x, 0.0, 0.0)) + sampleVol(p - vec3(e.x, 0.0, 0.0)) +
    sampleVol(p + vec3(0.0, e.y, 0.0)) + sampleVol(p - vec3(0.0, e.y, 0.0)) +
    sampleVol(p + vec3(0.0, 0.0, e.z)) + sampleVol(p - vec3(0.0, 0.0, e.z))
  ) * (1.0 / 6.0);
  return clamp(c + uSharp * 1.15 * (c - blur), 0.0, 1.0);
}

// Dither estable en espacio de volumen (rompe isocontornos 8-bit sin crawlear al orbitar).
float hash3(vec3 p) {
  p = fract(p * 0.1031);
  p += dot(p, p.yzx + 33.33);
  return fract((p.x + p.y) * p.z);
}

vec3 shadePresent(vec3 p, vec3 rgb, vec3 rd, vec3 g) {
  float gLen = length(g);
  vec3 n = normalize(g / max(uBox, vec3(0.08)) + vec3(1e-5));
  if (dot(n, -rd) < 0.0) n = -n;

  // Dirección de luz en espacio de vista → mundo (right/up/−fwd).
  vec3 lv = uLightView;
  float llen = length(lv);
  if (llen < 1e-4) lv = vec3(0.42, 0.72, 0.55);
  else lv /= llen;
  vec3 L1 = normalize(uRight * lv.x + uUp * lv.y + (-uFwd) * lv.z);
  vec3 L2 = normalize(uRight * (-lv.x * 0.55) + uUp * (lv.y * 0.35) + (-uFwd) * 0.25);
  float ndl1 = max(dot(n, L1), 0.0);
  float ndl2 = max(dot(n, L2), 0.0);
  float ndv = max(dot(n, -rd), 0.0);
  vec3 H = normalize(L1 - rd);
  float spec = pow(max(dot(n, H), 0.0), 32.0);
  float surf = smoothstep(0.04, 0.26, gLen);
  float amb = 0.18;
  float dif = 0.70 * ndl1 + 0.22 * ndl2;
  float cav = mix(1.0, 0.55 + 0.45 * ndl1, surf);
  float rim = pow(1.0 - ndv, 2.8) * 0.22 * surf;
  float lit = (amb + dif * surf) * cav + spec * 0.38 * surf;
  return rgb * lit + rgb * rim;
}

/** Clínico: casi plano, como el MPR (sin sombras de presentación). */
vec3 shadeClinical(vec3 rgb, float s) {
  return rgb * (0.86 + 0.14 * s);
}

vec3 applyMark(vec2 uv, vec3 rgb) {
  if (uMarkOn == 0) return rgb;
  vec2 ndc = vec2((uv.x - 0.5) * uAspect, uv.y - 0.5) - uPan;
  float dist = 1.85 / max(uZoom, 0.35);
  vec3 cam = vec3(0.5) - uFwd * dist;
  vec3 mw = (uMark - 0.5) * max(uBox, vec3(0.08)) + vec3(0.5);
  vec3 to = mw - cam;
  float lz = dot(to, uFwd);
  if (lz < 0.02) return rgb;
  float lx = dot(to, uRight);
  float ly = dot(to, uUp);
  vec2 muv = vec2((lx / (0.78 * lz) + uPan.x) / max(uAspect, 0.001) + 0.5, (ly / (0.78 * lz) + uPan.y) + 0.5);
  vec2 d = (uv - muv) * vec2(uAspect, 1.0);
  float r = length(d);
  float arm = 0.048;
  float thick = 0.0048;
  float hx = (1.0 - smoothstep(0.0, thick, abs(d.y))) * (1.0 - smoothstep(arm, arm + thick, abs(d.x)));
  float hy = (1.0 - smoothstep(0.0, thick, abs(d.x))) * (1.0 - smoothstep(arm, arm + thick, abs(d.y)));
  float ring = 1.0 - smoothstep(0.0, thick * 1.2, abs(r - 0.018));
  float core = 1.0 - smoothstep(0.0, 0.0065, r);
  float a = max(max(hx, hy), max(ring, core));
  return mix(rgb, vec3(0.12, 0.95, 1.0), clamp(a, 0.0, 1.0));
}

vec4 march(vec2 uv) {
  vec2 ndc = vec2((uv.x - 0.5) * uAspect, uv.y - 0.5) - uPan;
  float dist = 1.85 / max(uZoom, 0.35);
  vec3 cam = vec3(0.5) - uFwd * dist;
  vec3 rd = normalize(uFwd + (uRight * ndc.x + uUp * ndc.y) * 0.78);
  vec2 hit = hitBox(cam, rd);
  if (hit.y < hit.x || hit.y < 0.0) {
    return vec4(0.0, 0.0, 0.0, 1.0);
  }
  float t0 = max(hit.x, 0.0);
  float t1 = hit.y;
  float span = max(t1 - t0, 0.0001);
  float dt = span / max(float(uSteps), 1.0);
  // Jitter de inicio de rayo + dither en densidad: rompe isosuperficies 8-bit (“cebolla”).
  float j = hash3(vec3(uv * vec2(173.0, 91.0), uInvN * 17.0));
  vec3 pos = cam + rd * (t0 + dt * j);
  vec3 stepv = rd * dt;

  if (uMode == 0) {
    float acc = 0.0;
    bool any = false;
    for (int i = 0; i < 512; i++) {
      if (i >= uSteps) break;
      if (insideClip(pos)) {
        acc = max(acc, uSteps < 160 ? sampleVol(pos) : sampleSmooth(pos));
        any = true;
      }
      pos += stepv;
    }
    if (!any || acc < 0.004) return vec4(0.0, 0.0, 0.0, 1.0);
    float d = (hash3(vec3(uv * 41.0, acc * 13.0)) - 0.5) * (uShadeMode == 0 ? 0.05 : 0.03);
    return vec4(texture(uTf, vec2(clamp(acc + d, 0.0, 1.0), 0.5)).rgb, 1.0);
  }

  vec4 outc = vec4(0.0);
  float dtRef = max(uInvN * 0.42, 0.0008);
  float alphaScale = clamp(dt / dtRef, 0.3, 2.4);
  for (int i = 0; i < 512; i++) {
    if (i >= uSteps) break;
    if (outc.a > 0.88) break;
    if (insideClip(pos)) {
      float s = (uSharp >= 0.02)
        ? sampleSharp(pos)
        : (uSteps < 160 ? sampleVol(pos) : sampleSmooth(pos));
      float ditherAmp = 0.062;
      ditherAmp *= mix(1.0, 0.28, clamp(uSharp * 0.55, 0.0, 1.0));
      s = clamp(s + (hash3(pos / max(uInvN, 1e-5)) - 0.5) * ditherAmp, 0.0, 1.0);
      if (s > 0.022) {
        vec4 src = texture(uTf, vec2(s, 0.5));
        vec3 g = densGrad(pos);
        float gm = length(g);
        float edge = uShadeMode != 0
          ? smoothstep(0.048, 0.22, gm)
          : mix(0.18, 1.0, smoothstep(0.018, 0.12, gm));
        src.a *= edge;
        if (uShadeMode != 0) {
          // Presentación tipo Hueso: solo el frente de gradiente, no cascarones internos.
          src.a *= edge;
          src.a = min(1.0, pow(clamp(src.a, 0.0, 1.0), 0.62) * 2.15);
        } else {
          src.a = pow(clamp(src.a, 0.0, 1.0), 0.88);
        }
        if (src.a > 0.002) {
          if (uLight == 1) {
            if (uShadeMode == 0) src.rgb = shadeClinical(src.rgb, s);
            else src.rgb = shadePresent(pos, src.rgb, rd, g);
          }
          src.a = 1.0 - pow(max(1.0 - src.a, 0.0), alphaScale);
          outc.rgb += (1.0 - outc.a) * src.a * src.rgb;
          outc.a += (1.0 - outc.a) * src.a;
        }
      }
    }
    pos += stepv;
  }
  return vec4(outc.rgb, 1.0);
}

void main() {
  if (uVolOff == 1) {
    fragColor = vec4(applyMark(vUv, vec3(0.02)), 1.0);
    return;
  }
  vec4 col = march(vUv);
  col.rgb = applyMark(vUv, col.rgb);
  if (uSharp > 0.02) {
    float l = dot(col.rgb, vec3(0.299, 0.587, 0.114));
    float edge = length(vec2(dFdx(l), dFdy(l)));
    col.rgb *= 1.0 + uSharp * 0.18;
    col.rgb += vec3(edge * uSharp * 1.35);
    float g = hash3(vec3(gl_FragCoord.xy * 0.41, l * 11.0)) - 0.5;
    col.rgb += g * uSharp * 0.14 * smoothstep(0.025, 0.16, l);
    col.rgb = clamp(col.rgb, 0.0, 1.0);
  }
  fragColor = col;
}
`

function compile(gl, type, src) {
  const sh = gl.createShader(type)
  gl.shaderSource(sh, src)
  gl.compileShader(sh)
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(sh) || 'shader compile'
    gl.deleteShader(sh)
    throw new Error(log)
  }
  return sh
}

function link(gl, vsSrc, fsSrc) {
  const vs = compile(gl, gl.VERTEX_SHADER, vsSrc)
  const fs = compile(gl, gl.FRAGMENT_SHADER, fsSrc)
  const prog = gl.createProgram()
  gl.attachShader(prog, vs)
  gl.attachShader(prog, fs)
  gl.linkProgram(prog)
  gl.deleteShader(vs)
  gl.deleteShader(fs)
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
    const log = gl.getProgramInfoLog(prog) || 'program link'
    gl.deleteProgram(prog)
    throw new Error(log)
  }
  return prog
}

function loc(gl, prog, name) {
  return gl.getUniformLocation(prog, name)
}

/** @deprecated usar buildPresetTf('compuesto') */
export function buildBoneTf() {
  return buildPresetTf('compuesto')
}

export function hasWebGl2() {
  if (typeof document === 'undefined') return false
  try {
    const c = document.createElement('canvas')
    return !!c.getContext('webgl2')
  } catch (e) {
    return false
  }
}

function makeTfTexture(gl, pixels) {
  const tex = gl.createTexture()
  const width = Math.max(1, (pixels.length / 4) | 0)
  gl.bindTexture(gl.TEXTURE_2D, tex)
  gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, pixels)
  return tex
}

/**
 * @param {HTMLCanvasElement} canvas
 * @returns {null | {
 *   backend: 'gl',
 *   upload: function,
 *   draw: function,
 *   dispose: function,
 *   lost: boolean,
 * }}
 */
export function createGlVolumeRenderer(canvas) {
  if (!canvas || typeof canvas.getContext !== 'function') return null
  let gl
  try {
    gl = canvas.getContext('webgl2', {
      alpha: false,
      depth: false,
      stencil: false,
      antialias: false,
      premultipliedAlpha: true,
      preserveDrawingBuffer: true,
      powerPreference: 'high-performance',
    })
  } catch (e) {
    return null
  }
  if (!gl) return null

  let prog
  try {
    prog = link(gl, VS, FS)
  } catch (e) {
    return null
  }

  const u = {
    vol: loc(gl, prog, 'uVol'),
    tf: loc(gl, prog, 'uTf'),
    fwd: loc(gl, prog, 'uFwd'),
    right: loc(gl, prog, 'uRight'),
    up: loc(gl, prog, 'uUp'),
    zoom: loc(gl, prog, 'uZoom'),
    pan: loc(gl, prog, 'uPan'),
    aspect: loc(gl, prog, 'uAspect'),
    steps: loc(gl, prog, 'uSteps'),
    mode: loc(gl, prog, 'uMode'),
    decapado: loc(gl, prog, 'uDecapado'),
    light: loc(gl, prog, 'uLight'),
    lightView: loc(gl, prog, 'uLightView'),
    shadeMode: loc(gl, prog, 'uShadeMode'),
    clip: loc(gl, prog, 'uClip'),
    keep: loc(gl, prog, 'uKeep'),
    clipMask: loc(gl, prog, 'uClipMask'),
    slab: loc(gl, prog, 'uSlab'),
    invN: loc(gl, prog, 'uInvN'),
    box: loc(gl, prog, 'uBox'),
    mark: loc(gl, prog, 'uMark'),
    markOn: loc(gl, prog, 'uMarkOn'),
    sharp: loc(gl, prog, 'uSharp'),
    hu: loc(gl, prog, 'uHu'),
    mask: loc(gl, prog, 'uMask'),
    huOn: loc(gl, prog, 'uHuOn'),
    maskOn: loc(gl, prog, 'uMaskOn'),
    ww: loc(gl, prog, 'uWw'),
    wc: loc(gl, prog, 'uWc'),
    volOff: loc(gl, prog, 'uVolOff'),
  }

  let volTex = null
  let huTex = null
  let maskTex = null
  let dummy3d = null
  let volN = 0
  let volRef = null
  let volRev = -1
  let huRev = -1
  let maskRef = null
  let gpuHuOk = false
  let volBox = { x: 1, y: 1, z: 1 }

  const makeDummy3d = () => {
    const t = gl.createTexture()
    gl.bindTexture(gl.TEXTURE_3D, t)
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MIN_FILTER, gl.NEAREST)
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MAG_FILTER, gl.NEAREST)
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_R, gl.CLAMP_TO_EDGE)
    gl.texImage3D(gl.TEXTURE_3D, 0, gl.RG8, 1, 1, 1, 0, gl.RG, gl.UNSIGNED_BYTE, new Uint8Array([0, 0]))
    return t
  }
  dummy3d = makeDummy3d()
  const tfTex = makeTfTexture(gl, buildBoneTf())
  let lost = false
  const vao = gl.createVertexArray()
  const dummy = gl.createBuffer()
  gl.bindVertexArray(vao)
  gl.bindBuffer(gl.ARRAY_BUFFER, dummy)
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(3), gl.STATIC_DRAW)
  gl.bindVertexArray(null)

  const onLost = (ev) => {
    try { ev.preventDefault() } catch (e) { /* ignore */ }
    lost = true
  }
  canvas.addEventListener('webglcontextlost', onLost, false)

  const upload = (volume) => {
    if (lost || !volume || !volume.data) return false
    const rev = volume.rev != null ? Number(volume.rev) : 0
    if (volRef === volume && volTex && volRev === rev) return true
    const N = volume.size | 0
    if (!(N >= 8) || volume.data.length < N * N * N) return false

    // Remapeo WW/WC: misma textura, solo sustituir texels (mucho más rápido).
    if (volRef === volume && volTex && volN === N) {
      while (gl.getError()) { /* drain */ }
      gl.bindTexture(gl.TEXTURE_3D, volTex)
      gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1)
      try {
        gl.texSubImage3D(
          gl.TEXTURE_3D,
          0,
          0,
          0,
          0,
          N,
          N,
          N,
          gl.RED,
          gl.UNSIGNED_BYTE,
          volume.data
        )
        if (!gl.getError()) {
          volRev = rev
          return true
        }
      } catch (e) { /* fallback full upload */ }
    }

    if (volTex) {
      try { gl.deleteTexture(volTex) } catch (e) { /* ignore */ }
    }
    volTex = gl.createTexture()
    gl.bindTexture(gl.TEXTURE_3D, volTex)
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1)
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_R, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_BASE_LEVEL, 0)
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MAX_LEVEL, 0)
    const max3d = gl.getParameter(gl.MAX_3D_TEXTURE_SIZE) || 0
    if (max3d && N > max3d) {
      try { gl.deleteTexture(volTex) } catch (e) { /* ignore */ }
      volTex = null
      return false
    }
    while (gl.getError()) { /* drain */ }
    let uploaded = false
    try {
      gl.texImage3D(
        gl.TEXTURE_3D,
        0,
        gl.R8,
        N,
        N,
        N,
        0,
        gl.RED,
        gl.UNSIGNED_BYTE,
        volume.data
      )
      uploaded = !gl.getError()
    } catch (e) {
      uploaded = false
    }
    if (!uploaded) {
      try {
        const rgba = new Uint8Array(N * N * N * 4)
        const src = volume.data
        for (let i = 0, j = 0; i < N * N * N; i++, j += 4) {
          const v = src[i]
          rgba[j] = v
          rgba[j + 1] = v
          rgba[j + 2] = v
          rgba[j + 3] = 255
        }
        gl.texImage3D(
          gl.TEXTURE_3D,
          0,
          gl.RGBA8,
          N,
          N,
          N,
          0,
          gl.RGBA,
          gl.UNSIGNED_BYTE,
          rgba
        )
        uploaded = !gl.getError()
      } catch (e) {
        uploaded = false
      }
    }
    if (!uploaded) {
      try { gl.deleteTexture(volTex) } catch (e) { /* ignore */ }
      volTex = null
      return false
    }
    volN = N
    volRef = volume
    volRev = rev
    // WW/WC sigue en CPU (`remapVolumeWwwc`). No empaquetar HU→RG8 aquí:
    // duplica VRAM y retrasa el primer fotograma.
    gpuHuOk = false

    const b = volume.boxScale || {}
    const bx = Number(b.x)
    const by = Number(b.y)
    const bz = Number(b.z)
    volBox = {
      x: bx > 0.05 && bx <= 1 ? bx : 1,
      y: by > 0.05 && by <= 1 ? by : 1,
      z: bz > 0.05 && bz <= 1 ? bz : 1,
    }
    return true
  }

  const draw = (opts) => {
    if (lost || !volTex) return false
    try {
      const cssW = Math.max(64, opts.width | 0)
      const cssH = Math.max(64, opts.height | 0)
      const dpr = Math.min(2, Number(opts.dpr) || 1)
      const maxPx = 2048
      const fast = !!opts.fast
      let scale = Math.min(dpr, maxPx / Math.max(cssW, cssH, 1))
      if (fast) scale = Math.min(scale, opts.phone ? 0.55 : 0.75)
      const w = Math.max(64, Math.round(cssW * scale))
      const h = Math.max(64, Math.round(cssH * scale))
      if (canvas.width !== w) canvas.width = w
      if (canvas.height !== h) canvas.height = h
      canvas.style.width = `${cssW}px`
      canvas.style.height = `${cssH}px`

      const yaw = Number(opts.yaw) || 0
      const pitch = opts.pitch != null ? Number(opts.pitch) : 0.18
      const zoom = Math.max(0.35, Math.min(4.2, Number(opts.zoom) || 1))
      const panX = Math.max(-1.15, Math.min(1.15, Number(opts.panX) || 0))
      const panY = Math.max(-1.15, Math.min(1.15, Number(opts.panY) || 0))
      const { fx, fy, fz, rx, ry, rz, ux, uy, uz } = mpr3dViewBasis(
        yaw,
        pitch,
        !!opts.flipX,
        !!opts.flipY
      )

      const N = volN || 128
      const steps = fast
        ? Math.max(48, opts.phone ? 96 : 128)
        : Math.max(64, Math.min(512, opts.phone ? 384 : 480))

      const clip = opts.clip || { x: 0, y: 0, z: 0 }
      const keep = opts.keep || { x: 1, y: 1, z: 1 }
      const n1 = Math.max(1, N - 1)
      const cx = Math.max(0, Math.min(1, (Number(clip.x) || 0) / n1))
      const cy = Math.max(0, Math.min(1, (Number(clip.y) || 0) / n1))
      const cz = Math.max(0, Math.min(1, (Number(clip.z) || 0) / n1))

      gl.viewport(0, 0, w, h)
      gl.disable(gl.DEPTH_TEST)
      gl.disable(gl.BLEND)
      gl.clearColor(0, 0, 0, 1)
      gl.clear(gl.COLOR_BUFFER_BIT)
      gl.useProgram(prog)
      gl.bindVertexArray(vao)

      gl.activeTexture(gl.TEXTURE0)
      gl.bindTexture(gl.TEXTURE_3D, volTex)
      gl.uniform1i(u.vol, 0)
      gl.activeTexture(gl.TEXTURE1)
      gl.bindTexture(gl.TEXTURE_2D, tfTex)
      gl.uniform1i(u.tf, 1)
      gl.activeTexture(gl.TEXTURE2)
      gl.bindTexture(gl.TEXTURE_3D, gpuHuOk && huTex ? huTex : dummy3d)
      gl.uniform1i(u.hu, 2)
      gl.activeTexture(gl.TEXTURE3)
      gl.bindTexture(gl.TEXTURE_3D, maskTex || dummy3d)
      gl.uniform1i(u.mask, 3)
      gl.uniform1i(u.huOn, gpuHuOk && !opts.phone ? 1 : 0)
      gl.uniform1i(u.maskOn, maskTex && opts.maskOn ? 1 : 0)
      gl.uniform1f(u.ww, Math.max(1, Number(opts.ww) || 400))
      gl.uniform1f(u.wc, Number.isFinite(Number(opts.wc)) ? Number(opts.wc) : 40)
      gl.uniform1i(u.volOff, opts.volOff ? 1 : 0)

      gl.uniform3f(u.fwd, fx, fy, fz)
      gl.uniform3f(u.right, rx, ry, rz)
      gl.uniform3f(u.up, ux, uy, uz)
      gl.uniform1f(u.zoom, zoom)
      gl.uniform2f(u.pan, panX, panY)
      gl.uniform1f(u.aspect, cssH > 0 ? cssW / cssH : 1)
      gl.uniform1i(u.steps, steps)
      gl.uniform1i(u.mode, opts.mode === 'mip' ? 0 : 1)
      gl.uniform1i(u.decapado, opts.decapado ? 1 : 0)
      const shadeMode = opts.shadeMode === 'clinical' || opts.shadeMode === 0 ? 0 : 1
      gl.uniform1i(u.light, opts.mode === 'mip' ? 0 : 1)
      gl.uniform1i(u.shadeMode, opts.mode === 'mip' ? 0 : shadeMode)
      gl.uniform1f(u.sharp, Math.max(0, Math.min(4.0, Number(opts.sharp) || 0)))
      const lv = opts.lightView || { x: 0.42, y: 0.72, z: 0.55 }
      gl.uniform3f(u.lightView, Number(lv.x) || 0, Number(lv.y) || 0, Number(lv.z) || 1)
      gl.uniform3f(u.clip, cx, cy, cz)
      gl.uniform3f(
        u.keep,
        keep.x >= 0 ? 1 : -1,
        keep.y >= 0 ? 1 : -1,
        keep.z >= 0 ? 1 : -1
      )
      const mask = opts.clipMask || { x: 1, y: 1, z: 1 }
      gl.uniform3f(
        u.clipMask,
        mask.x > 0 ? 1 : 0,
        mask.y > 0 ? 1 : 0,
        mask.z > 0 ? 1 : 0
      )
      gl.uniform1f(u.invN, 1 / N)
      const slabVox = Math.max(0, Number(opts.slab) || 0)
      gl.uniform1f(u.slab, slabVox > 0.5 ? slabVox / n1 : 0.0)
      gl.uniform3f(u.box, volBox.x, volBox.y, volBox.z)
      const mark = opts.mark || { x: 0, y: 0, z: 0 }
      gl.uniform3f(
        u.mark,
        Math.max(0, Math.min(1, (Number(mark.x) || 0) / n1)),
        Math.max(0, Math.min(1, (Number(mark.y) || 0) / n1)),
        Math.max(0, Math.min(1, (Number(mark.z) || 0) / n1))
      )
      gl.uniform1i(u.markOn, opts.markOn ? 1 : 0)

      gl.drawArrays(gl.TRIANGLES, 0, 3)
      return true
    } catch (e) {
      return false
    }
  }

  const setTf = (pixels) => {
    if (lost || !tfTex || !pixels) return false
    const width = Math.max(1, (pixels.length / 4) | 0)
    gl.bindTexture(gl.TEXTURE_2D, tfTex)
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1)
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, pixels)
    return !gl.getError()
  }

  const dispose = () => {
    canvas.removeEventListener('webglcontextlost', onLost, false)
    try {
      if (volTex) gl.deleteTexture(volTex)
      if (huTex) gl.deleteTexture(huTex)
      if (maskTex) gl.deleteTexture(maskTex)
      if (dummy3d) gl.deleteTexture(dummy3d)
      if (tfTex) gl.deleteTexture(tfTex)
      if (dummy) gl.deleteBuffer(dummy)
      if (vao) gl.deleteVertexArray(vao)
      if (prog) gl.deleteProgram(prog)
    } catch (e) { /* ignore */ }
    volTex = null
    volRef = null
    volRev = -1
    lost = true
    const ext = gl.getExtension('WEBGL_lose_context')
    if (ext) {
      try { ext.loseContext() } catch (e) { /* ignore */ }
    }
  }

  const uploadMask = (mask, n) => {
    if (lost) return false
    const N = n | 0
    if (!mask || !(N >= 8) || mask.length < N * N * N) {
      if (maskTex) {
        try { gl.deleteTexture(maskTex) } catch (e) { /* ignore */ }
        maskTex = null
      }
      maskRef = null
      return true
    }
    if (maskRef === mask && maskTex) return true
    if (maskTex) {
      try { gl.deleteTexture(maskTex) } catch (e) { /* ignore */ }
    }
    maskTex = gl.createTexture()
    gl.bindTexture(gl.TEXTURE_3D, maskTex)
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1)
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MIN_FILTER, gl.NEAREST)
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MAG_FILTER, gl.NEAREST)
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_R, gl.CLAMP_TO_EDGE)
    try {
      gl.texImage3D(gl.TEXTURE_3D, 0, gl.R8, N, N, N, 0, gl.RED, gl.UNSIGNED_BYTE, mask)
      if (gl.getError()) {
        gl.deleteTexture(maskTex)
        maskTex = null
        return false
      }
    } catch (e) {
      maskTex = null
      return false
    }
    maskRef = mask
    return true
  }

  return {
    backend: 'gl',
    upload,
    uploadMask,
    draw,
    setTf,
    dispose,
    get lost() {
      return lost
    },
    get gpuHu() {
      return gpuHuOk
    },
  }
}
